import { ApiError } from "@/lib/api/client";
import { getApiErrorCode } from "@/lib/api/work-items";
import { groomingApiErrorCode } from "@/lib/grooming/error-messages";

/**
 * Why a live Azure DevOps read failed, in words a Project Manager can act on
 * (epic PI-SPEC-SW-E290, feature PI-SPEC-SW-1480, UX-PM-37; Claude Design
 * "PM Packages B E F Mockups" frame B6).
 *
 * The Backlog, Session build and the reading pane read Azure DevOps live
 * through SpecTree. When that read fails they show one card (`AdoReadErrorCard`)
 * naming the cause, what was already tried, and one thing to do. No API
 * change: every cause comes from a code the API already returns
 * (`packages/api/src/services/tracker/trackerErrors.ts`):
 *
 * | API code (HTTP)                          | Cause            |
 * |------------------------------------------|------------------|
 * | ADO_REQUEST_FAILED (502), status 0/502/503/504 | `ado-no-reply` |
 * | ADO_REQUEST_FAILED (502), status 401/403 | `no-access`      |
 * | ADO_PAT_INVALID / ADO_PAT_REQUIRED (400) | `token`          |
 * | ADO_RATE_LIMITED (429)                   | `rate-limited`   |
 * | fetch TypeError, or the browser offline  | `offline`        |
 * | GROOMING_CONFIG_NOT_FOUND                | `not-set-up`     |
 * | NOT_FOUND / WORK_ITEM_NOT_FOUND          | `not-found`      |
 * | anything else (ADO_CODE_READ_REFUSED, 5xx from SpecTree, …) | `other` |
 *
 * A 401 from SpecTree itself is an expired session: the API client logs the
 * person out, so it never reaches the card. Pure, React-free.
 */

export type AdoReadCause =
  | "ado-no-reply"
  | "offline"
  | "token"
  | "no-access"
  | "rate-limited"
  | "not-set-up"
  | "not-found"
  | "other";

/** What a failed read is, as the card needs it. */
export interface AdoReadFailure {
  cause: AdoReadCause;
  /** The API's most specific error code, or null (a network failure, or no error yet). */
  code: string | null;
  /** The HTTP status Azure DevOps gave SpecTree (`details.status` of ADO_REQUEST_FAILED); 0 when it did not reply. */
  adoStatus: number | null;
  /** The error's own message, for "Copy details for the Admin" only. */
  message: string | null;
}

/** ADO statuses that mean Azure DevOps did not answer (0: no reply at all). */
const NO_REPLY_STATUSES: ReadonlySet<number> = new Set([0, 502, 503, 504]);
/** ADO statuses that mean the token reaches ADO but may not read the project. */
const NO_ACCESS_STATUSES: ReadonlySet<number> = new Set([401, 403]);
const TOKEN_CODES: ReadonlySet<string> = new Set(["ADO_PAT_INVALID", "ADO_PAT_REQUIRED"]);
const NOT_FOUND_CODES: ReadonlySet<string> = new Set(["NOT_FOUND", "WORK_ITEM_NOT_FOUND"]);

/** `error.details.status` of a failed SpecTree request, when it is a number. */
export function adoStatusOf(error: unknown): number | null {
  if (!(error instanceof ApiError)) return null;
  const data = error.data;
  if (!data || typeof data !== "object" || !("error" in data)) return null;
  const details = (data as { error?: { details?: unknown } }).error?.details;
  if (!details || typeof details !== "object" || !("status" in details)) return null;
  const status = (details as { status?: unknown }).status;
  return typeof status === "number" && Number.isFinite(status) ? status : null;
}

/** True for the TypeError `fetch` throws when the request never left this computer. */
function isNetworkFailure(error: unknown): boolean {
  return error instanceof TypeError;
}

/** Whether the browser says it is online; true when it cannot tell (tests, server rendering). */
export function browserOnline(): boolean {
  return typeof navigator === "undefined" || navigator.onLine;
}

/**
 * Classify a failed Azure DevOps read. `online` is the browser's own view
 * (`navigator.onLine`); offline wins over any error, because nothing else can
 * be known then. A null `error` with the browser online reads as `other`.
 */
export function classifyAdoReadError(error: unknown, online: boolean = browserOnline()): AdoReadFailure {
  const code = groomingApiErrorCode(error) ?? null;
  const topCode = getApiErrorCode(error) ?? null;
  const adoStatus = adoStatusOf(error);
  const message = error instanceof Error ? error.message : null;
  const failure = (cause: AdoReadCause): AdoReadFailure => ({ cause, code, adoStatus, message });

  if (!online || isNetworkFailure(error)) return failure("offline");
  const codes = [code, topCode].filter((value): value is string => value !== null);
  if (codes.some((value) => TOKEN_CODES.has(value))) return failure("token");
  if (codes.includes("ADO_RATE_LIMITED")) return failure("rate-limited");
  if (codes.includes("ADO_REQUEST_FAILED")) {
    if (adoStatus !== null && NO_REPLY_STATUSES.has(adoStatus)) return failure("ado-no-reply");
    if (adoStatus !== null && NO_ACCESS_STATUSES.has(adoStatus)) return failure("no-access");
    return failure("other");
  }
  if (codes.includes("GROOMING_CONFIG_NOT_FOUND")) return failure("not-set-up");
  if (codes.some((value) => NOT_FOUND_CODES.has(value))) return failure("not-found");
  return failure("other");
}

/**
 * Whether the one automatic retry (`retryTransientOnce`) already happened:
 * TanStack Query's `failureCount` is 2 after a failure, a retry and a second
 * failure, and 1 when the error was not retried (a PAT error, a throttled
 * account, any other client error). Only causes the retry covers can say so.
 */
export function automaticRetryHappened(failure: AdoReadFailure, failureCount: number): boolean {
  if (failureCount < 2) return false;
  return failure.cause === "ado-no-reply" || failure.cause === "other" || failure.cause === "offline";
}

/** What the read was for: the card's sentences name it. */
export type AdoReadSubject = "backlog" | "item";

/** The one primary thing the card offers. */
export type AdoReadAction = "try-again" | "renew-token" | "copy-message" | "none";

/** One line of the "what works, what didn't" checklist. */
export interface AdoReadCheck {
  ok: boolean;
  text: string;
}

/** Everything the card says, worked out without React. */
export interface AdoReadCopy {
  /** The card's heading; null for the causes that keep a single plain sentence (not set up, not found). */
  title: string | null;
  body: string;
  checklist: readonly AdoReadCheck[];
  primary: AdoReadAction;
  /** Try again as a quiet second button (the no-access card, after access is given). */
  secondaryTryAgain: boolean;
  /** Whether "Copy details for the Admin" is offered. */
  copyDetails: boolean;
}

export interface AdoReadCopyContext {
  subject: AdoReadSubject;
  /** The one automatic retry already happened ({@link automaticRetryHappened}). */
  retried: boolean;
  /** The ADO project, for the no-access sentence. */
  project?: string | null | undefined;
  /** The ADO organisation name ("rivcode"), when known. */
  organization?: string | null | undefined;
}

/** Today's words for a project not set up for grooming (kept from before B6). */
export const NOT_SET_UP_SENTENCE =
  "This project is not set up for grooming yet. A Project Manager or an Admin sets it up on the Project page.";
/** The reading pane's words for an item ADO does not have (kept from before B6). */
export const NOT_FOUND_SENTENCE = "This item was not found in ADO.";

const RETRIED_SENTENCE = "The Toolbox already tried once more by itself.";

function subjectWords(subject: AdoReadSubject): { the: string; lower: string } {
  return subject === "backlog" ? { the: "The backlog", lower: "the backlog" } : { the: "This item", lower: "this item" };
}

function projectWords(project: string | null | undefined): string {
  return project && project.length > 0 ? project : "this project";
}

/** "Azure DevOps (dev.azure.com/rivcode)" when the organisation is known. */
function adoWords(organization: string | null | undefined): string {
  return organization && organization.length > 0 ? `Azure DevOps (dev.azure.com/${organization})` : "Azure DevOps";
}

/** The B6 card's words for a failure. */
export function adoReadCopy(failure: AdoReadFailure, context: AdoReadCopyContext): AdoReadCopy {
  const words = subjectWords(context.subject);
  const retried = context.retried ? ` ${RETRIED_SENTENCE}` : "";
  const base = { checklist: [] as AdoReadCheck[], secondaryTryAgain: false, copyDetails: true };

  switch (failure.cause) {
    case "ado-no-reply": {
      const noReply = failure.adoStatus === 0 || failure.adoStatus === null;
      return {
        ...base,
        title: "Azure DevOps didn't answer",
        body: `${words.the} couldn't be read: ${
          noReply ? "Azure DevOps took too long to reply." : "Azure DevOps is not available right now."
        }${retried} This is usually over in a minute.`,
        checklist: [
          { ok: true, text: "Your connection to SpecTree works" },
          {
            ok: false,
            text: `${adoWords(context.organization)} ${noReply ? "did not reply" : "was not available"}${
              context.retried ? ", twice" : ""
            }`,
          },
        ],
        primary: "try-again",
      };
    }
    case "offline":
      return {
        ...base,
        title: "No connection",
        body: `This computer is offline. ${words.the} loads by itself when it's back.`,
        checklist: [{ ok: false, text: "This computer can't reach SpecTree" }],
        primary: "try-again",
        copyDetails: false,
      };
    case "token":
      return {
        ...base,
        title: "Renew your Azure DevOps token",
        body:
          failure.code === "ADO_PAT_REQUIRED"
            ? `Connect Azure DevOps to read ${words.lower}.`
            : "Your Azure DevOps token has run out.",
        primary: "renew-token",
        copyDetails: false,
      };
    case "no-access": {
      const project = projectWords(context.project);
      return {
        ...base,
        title: `No access to ${project}`,
        body: `Your token can't read ${project}. Ask your Project Manager or an Admin for access.`,
        primary: "copy-message",
        secondaryTryAgain: true,
        copyDetails: false,
      };
    }
    case "rate-limited":
      return {
        ...base,
        title: "Azure DevOps asked the Toolbox to wait",
        body: "Azure DevOps is slow right now and asked the Toolbox to wait. Try again in a minute.",
        primary: "try-again",
      };
    case "not-set-up":
      return { ...base, title: null, body: NOT_SET_UP_SENTENCE, primary: "none", copyDetails: false };
    case "not-found":
      return { ...base, title: null, body: NOT_FOUND_SENTENCE, primary: "none", copyDetails: false };
    case "other":
      return {
        ...base,
        title: "Something went wrong",
        body: `${words.the} couldn't be read.${retried}`,
        primary: "try-again",
      };
  }
}

/** The no-access card's "Copy a message": a note to whoever can give access. */
export function accessRequestMessage(project: string | null | undefined, email: string | null | undefined): string {
  const name = projectWords(project);
  const account = email && email.length > 0 ? ` My account is ${email}.` : "";
  return `Hi, could you give my Azure DevOps account access to ${name}? The SpecTree Toolbox says my token cannot read it.${account}`;
}

/** "Copy details for the Admin": what failed, in plain words. */
export function adoReadWhat(subject: AdoReadSubject, failure: AdoReadFailure): string {
  const what = subject === "backlog" ? "The backlog couldn't be read from Azure DevOps" : "An item couldn't be read from Azure DevOps";
  return failure.adoStatus === null ? what : `${what} (Azure DevOps HTTP ${String(failure.adoStatus)})`;
}
